import "@progress/kendo-ui/js/kendo.upload";
import * as ko from "knockout";
import * as Global from "../common/global";
import * as Data from "../data/templatePhraseImportData";
import { IApplication } from "../interfaces/application";
import * as UI from "./ui";

enum StateValues {
    Hidden,
    UploadFile,
    Validate,
    ImportFinished,
}

interface IViewModel {
    State: KnockoutObservable<StateValues>;
    FileIsUploaded: KnockoutObservable<boolean>;
    AppendToExistingPhrases: KnockoutObservable<boolean>;
    ValidPhrases: KnockoutObservable<Data.ITemplatePhraseInfo[]>;
    InvalidPhrases: KnockoutObservable<Data.ITemplatePhraseInfo[]>;
    ImportedPhrases: KnockoutObservable<Data.ITemplatePhraseInfo[]>;
    NotImportedPhrases: KnockoutObservable<Data.ITemplatePhraseInfo[]>;
    GeneralErrors: KnockoutObservable<string[]>;
    hasValidPhrases: () => boolean;
    hasInvalidPhrases: () => boolean;
    onCancel: () => void;
    onValidateFile: () => void;
    onImportFile: () => void;
    onImportFinished: () => void;
    isImported: () => boolean;
    isNotImported: () => boolean;
    isStateHidden: () => boolean;
    isStateUploadFile: () => boolean;
    isStateValidate: () => boolean;
    isStateImportFinished: () => boolean;
    hasGeneralErrors: () => boolean;
    numberOfGeneralErrors: () => number;
    numberOfImportedPhrases: () => number;
    numberOfNotImportedPhrases: () => number;
    numberOfInvalidPhrases: () => number;
    numberOfValidPhrases: () => number;
    BackToUploadFile: () => void;
}

export class TemplatePhraseImportControl {
    private container: JQuery;
    private viewModel: IViewModel;
    private uploadSessionID: number;
    private uploadControl: kendo.ui.Upload;
    private templatePhraseID: number;
    private onCancel: () => void;
    private onImportFinished: () => void;

    constructor(onCancel: () => void, onImportFinished: () => void) {
        this.onCancel = onCancel;
        this.onImportFinished = onImportFinished;

        this.viewModel = {
            State: ko.observable(StateValues.Hidden),
            FileIsUploaded: ko.observable(false),
            AppendToExistingPhrases: ko.observable(true),
            ValidPhrases: ko.observableArray(),
            InvalidPhrases: ko.observableArray(),
            ImportedPhrases: ko.observableArray(),
            NotImportedPhrases: ko.observableArray(),
            GeneralErrors: ko.observableArray(),
            BackToUploadFile: this.backToFileUpload.bind(this),

            numberOfValidPhrases(): number {
                const validPhrases = this.ValidPhrases();
                if (validPhrases) {
                    return validPhrases.length;
                } else {
                    return 0;
                }
            },
            hasValidPhrases(): boolean {
                return this.numberOfValidPhrases() > 0;
            },
            numberOfInvalidPhrases(): number {
                const invalidPhrases = this.InvalidPhrases();
                if (invalidPhrases) {
                    return invalidPhrases.length;
                } else {
                    return 0;
                }
            },
            hasInvalidPhrases(): boolean {
                return this.numberOfInvalidPhrases() > 0;
            },
            numberOfGeneralErrors(): number {
                const generalErrors = this.GeneralErrors();
                if (generalErrors) {
                    return generalErrors.length;
                } else {
                    return 0;
                }
            },
            hasGeneralErrors(): boolean {
                return this.numberOfGeneralErrors() > 0;
            },
            numberOfImportedPhrases(): number {
                const importedPhrases = this.ImportedPhrases();
                if (importedPhrases) {
                    return importedPhrases.length;
                } else {
                    return 0;
                }
            },
            isImported(): boolean {
                return this.numberOfImportedPhrases() > 0;
            },
            numberOfNotImportedPhrases(): number {
                const notImportedPhrases = this.NotImportedPhrases();
                if (notImportedPhrases) {
                    return notImportedPhrases.length;
                } else {
                    return 0;
                }
            },
            isNotImported(): boolean {
                return this.numberOfNotImportedPhrases() > 0;
            },
            onCancel: () => {
                if (this.onCancel) {
                    this.viewModel.State(StateValues.Hidden);
                    this.viewModel.FileIsUploaded(false);
                    this.reset();
                    this.onCancel();
                }
            },
            onValidateFile: () => {
                this.validateFile();
                this.viewModel.State(StateValues.Validate);
            },
            onImportFile: () => {
                if (this.viewModel.AppendToExistingPhrases()) {
                    UI.showConfirmDialog(`Are you sure you want to add ${this.viewModel.numberOfValidPhrases()} phrase details to the template?
                    This action can not be stopped once it has started.`,
                        () => this.importFile());
                } else {
                    UI.showConfirmDialog(`Are you sure you want to delete all current phrase details and replace them with
                    ${this.viewModel.numberOfValidPhrases()} phrase details? This action can not be stopped once it has started.`,
                        () => this.importFile());
                }
            },
            onImportFinished: () => {
                if (this.onImportFinished) {
                    this.viewModel.State(StateValues.Hidden);
                    this.reset();
                    this.onImportFinished();
                }
            },
            isStateHidden: () => this.viewModel.State() === StateValues.Hidden,
            isStateUploadFile: () => this.viewModel.State() === StateValues.UploadFile,
            isStateValidate: () => this.viewModel.State() === StateValues.Validate,
            isStateImportFinished: () => this.viewModel.State() === StateValues.ImportFinished,
        };
    }

    public isUploadInProcess(): boolean {
        return this.viewModel.FileIsUploaded() && !this.viewModel.isStateImportFinished();
    }

    public render(container: JQuery): JQueryPromise<any> {
        const app: IApplication = Global.app();
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();

        this.container = container;

        app.templates().render("templatephraseimportcontrol", "template_phrase_import_control", this.container).done(() => {
            ko.applyBindings(this.viewModel, container[0]);
            this.createUploadFileControl();
            deferred.resolve();
        });
        return deferred.promise();
    }

    public load(param: number): JQueryPromise<any> {
        if (param != null && param > 0) {
            this.templatePhraseID = param;
        } else {
            this.templatePhraseID = 0;
        }
        this.uploadSessionID = this.setUploadSessionID();
        const deferred: JQuery.Deferred<any> = $.Deferred();
        this.viewModel.State(StateValues.UploadFile);

        return deferred.promise();
    }

    private validateFile() {
        const request = this.buildRequest();
        Data.ValidateFile(request).done((response) => {
            if (response.Success) {
                this.viewModel.ValidPhrases.push.apply(this.viewModel.ValidPhrases, response.Payload.ValidPhrases);
                this.viewModel.InvalidPhrases.push.apply(this.viewModel.InvalidPhrases, response.Payload.InvalidPhrases);
                this.viewModel.GeneralErrors.push.apply(this.viewModel.GeneralErrors, response.Payload.ErrorMessages);
            } else {
                this.viewModel.GeneralErrors.push.apply(this.viewModel.GeneralErrors, response.ErrorMessages);
                this.viewModel.GeneralErrors.push.apply(this.viewModel.GeneralErrors, response.Payload.ErrorMessages);
            }
            this.viewModel.State(StateValues.Validate); // do this after validation
        });
    }

    private importFile() {
        const request = this.buildRequest();
        Data.Import(request).done((response) => {
            if (response.Success) {
                this.viewModel.ValidPhrases.push.apply(this.viewModel.ValidPhrases, response.Payload.ValidPhrases);
                this.viewModel.InvalidPhrases.push.apply(this.viewModel.InvalidPhrases, response.Payload.InvalidPhrases);
                this.viewModel.GeneralErrors.push.apply(this.viewModel.GeneralErrors, response.Payload.ErrorMessages);
            } else {
                this.viewModel.GeneralErrors.push.apply(this.viewModel.GeneralErrors, response.ErrorMessages);
                this.viewModel.GeneralErrors.push.apply(this.viewModel.GeneralErrors, response.Payload.ErrorMessages);
            }
            this.viewModel.ImportedPhrases.push.apply(this.viewModel.ImportedPhrases, response.Payload.ValidPhrases);
            this.viewModel.NotImportedPhrases.push.apply(this.viewModel.NotImportedPhrases, response.Payload.InvalidPhrases);
        });
        this.viewModel.State(StateValues.ImportFinished);

    }

    private setUploadSessionID() {
        // Use the number of milliseconds as the unique upload ID
        return this.uploadSessionID = (new Date()).getTime();
    }

    private clearUploadedFileList() {
        // Remove list of uploaded files from kendo control
        this.uploadControl.clearAllFiles();
    }

    private buildRequest(): Data.ITemplatePhraseImportRequest {
        const request: Data.ITemplatePhraseImportRequest = {
            AppendToExistingPhrases: this.viewModel.AppendToExistingPhrases(),
            TemplatePhraseID: this.templatePhraseID,
            UploadSessionID: this.uploadSessionID,
        };
        return request;
    }

    private createUploadFileControl() {
        this.uploadControl = this.container.find("input[data-id='uploadFileControl']").kendoUpload({
            async: {
                saveUrl: "save",
                autoUpload: true,
            },
            multiple: false,
            validation: {
                allowedExtensions: [".csv", ".xls", ".xlsx"],
            },
            upload: (e: kendo.ui.UploadUploadEvent) => {
                if (e.sender.options.async) {
                    e.sender.options.async.saveUrl = Global.app().webServiceBaseURL + `/api/FileUploadApi/Import/${this.setUploadSessionID()}`;
                }
            },
            // fires when all uploads are complete
            success: (e: kendo.ui.UploadSuccessEvent) => {
                this.viewModel.FileIsUploaded(true);
            },
        }).data("kendoUpload");
    }
    private backToFileUpload() {
        this.reset();
        this.viewModel.State(StateValues.UploadFile);
    }

    private reset() {
        this.clearUploadedFileList();
        this.viewModel.GeneralErrors([]);
        this.viewModel.ValidPhrases([]);
        this.viewModel.InvalidPhrases([]);
        this.viewModel.NotImportedPhrases([]);
        this.viewModel.ImportedPhrases([]);
        this.uploadSessionID = this.setUploadSessionID();
        this.viewModel.AppendToExistingPhrases(true);
        this.viewModel.FileIsUploaded(false);
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--7!./src/script/common/templatePhraseImport.ts